import { CollectionTable, CollectionCards } from '../../app/collection-view.js';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  CurrencyInput,
  DateRangePicker,
  FormErrorSummary,
  IconButton,
  Input,
  Modal,
  Select,
  StatusPill,
  Textarea,
  formatCivilRange,
  formatCurrency,
  todayCivilDate,
  useSnackbar,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock3, Coins, PencilLine, RotateCcw, ShoppingBag, Sparkles, Archive } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import {
  getApiError,
  servicesApi,
  type Currency,
  type ServiceActivityFilter,
  type ServiceInput,
  type ServiceOffering,
  type ServiceSort,
} from '../../lib/api.js';
import { formatDate } from '../../lib/time-zone.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  FilterGroup,
  LoadingCard,
  Pagination,
  RequestAlert,
  SortBar,
  SortableColumn,
} from '../../app/data-view.js';
import type { ServicesView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import { CreateButton, PageHeader } from '../../app/page-title.js';
import { ServiceUnitTermsFields, type ServiceUnit } from './service-unit-terms.js';

export interface ServiceCapabilities {
  canCreate: boolean;
  canUpdate: boolean;
  canArchive: boolean;
  /// Atalho para vender o serviço, presente só quando a empresa contratou Vendas e a pessoa pode
  /// registrar venda. Serviços não conhece Vendas: quem liga o atalho é a casca da tela.
  onSell?: ((service: ServiceOffering) => void) | undefined;
}

const serviceSchema = z.object({
  displayName: z.string().trim().min(2, 'Informe o nome do serviço.').max(160),
  description: z.string().trim().max(1000, 'Use até 1000 caracteres.'),
  durationMinutes: z
    .number()
    .int('Informe a duração em minutos inteiros.')
    .min(5, 'A duração mínima é de 5 minutos.')
    .max(1440, 'A duração máxima é de 24 horas.'),
  basePriceCents: z
    .number()
    .int('Informe o preço em centavos inteiros.')
    .min(0, 'O preço não pode ser negativo.')
    .max(2_000_000_000, 'O preço informado é muito alto.'),
  currency: z.enum(['BRL', 'USD', 'ARS']),
  unitTerms: z.array(
    z.object({
      businessUnitId: z.string(),
      priceCents: z.number().int().min(0).max(2_000_000_000).nullable(),
      durationMinutes: z
        .number()
        .int('Informe a duração de cada unidade em minutos inteiros.')
        .min(5, 'A duração de cada unidade vai de 5 minutos a 24 horas.')
        .max(1440, 'A duração de cada unidade vai de 5 minutos a 24 horas.')
        .nullable(),
    }),
  ),
});
type ServiceForm = z.infer<typeof serviceSchema>;

const activityOptions: Array<{ value: ServiceActivityFilter; label: string }> = [
  { value: 'ALL', label: 'Todos' },
  { value: 'ACTIVE', label: 'Ativos' },
  { value: 'INACTIVE', label: 'Arquivados' },
];
const currencyOptions: Array<{ value: Currency | 'ALL'; label: string }> = [
  { value: 'ALL', label: 'Todas' },
  { value: 'BRL', label: 'Real' },
  { value: 'USD', label: 'Dólar' },
  { value: 'ARS', label: 'Peso argentino' },
];
const formCurrencyOptions = currencyOptions.slice(1) as Array<{ value: Currency; label: string }>;
const columns: Array<{ sort: ServiceSort; label: string }> = [
  { sort: 'displayName', label: 'Serviço' },
  { sort: 'durationMinutes', label: 'Duração' },
  { sort: 'basePriceCents', label: 'Preço-base' },
  { sort: 'createdAt', label: 'Cadastrado em' },
];

export function CompanyServices({
  capabilities,
  timeZone,
  units = [],
  view,
  onView,
  serviceExtras,
}: {
  capabilities: ServiceCapabilities;
  timeZone: string;
  /// O que outro módulo acrescenta ao serviço já cadastrado, ligado pela composição: a Agenda põe
  /// a regra de cancelamento do serviço (ADR 0071). Serviços não conhece quem acrescenta.
  serviceExtras?: ((service: ServiceOffering) => ReactNode) | undefined;
  /// Estabelecimentos que podem ter preço e duração próprios (ADR 0074).
  units?: readonly ServiceUnit[];
  view: ServicesView;
  onView: ViewSetter<ServicesView>;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [createOpen, setCreateOpen] = useState(false);
  const [activationTarget, setActivationTarget] = useState<ServiceOffering>();
  const [activationError, setActivationError] = useState<string>();
  const filters = {
    ...(view.search.trim() ? { search: view.search.trim() } : {}),
    activity: view.activity,
    currency: view.currency,
    ...(view.from ? { from: view.from } : {}),
    ...(view.to ? { to: view.to } : {}),
    sort: view.sort,
    direction: view.direction,
    limit: view.limit,
    offset: view.offset,
  };
  const page = useQuery({
    queryKey: ['company', 'services', filters],
    queryFn: () => servicesApi.list(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const detail = useQuery({
    queryKey: ['company', 'services', view.service],
    queryFn: () => servicesApi.get(view.service!),
    enabled: Boolean(view.service),
    retry: false,
  });

  function toggleSort(column: ServiceSort) {
    if (view.sort === column) onView({ direction: view.direction === 'asc' ? 'desc' : 'asc' });
    else onView({ sort: column, direction: column === 'createdAt' ? 'desc' : 'asc' });
  }

  async function setActive(service: ServiceOffering) {
    setActivationError(undefined);
    try {
      await servicesApi.setActive(service.id, !service.active);
      await queryClient.invalidateQueries({ queryKey: ['company', 'services'] });
      setActivationTarget(undefined);
      snackbar.success(service.active ? 'Serviço arquivado.' : 'Serviço reativado.');
    } catch (error) {
      setActivationError(getApiError(error));
    }
  }

  const total = page.data?.total ?? 0;
  const chips = [
    ...(view.activity === 'ALL'
      ? []
      : [
          {
            key: 'activity',
            label: activityOptions.find((item) => item.value === view.activity)!.label,
            onRemove: () => onView({ activity: 'ALL' }),
          },
        ]),
    ...(view.currency === 'ALL'
      ? []
      : [
          {
            key: 'currency',
            label: currencyOptions.find((item) => item.value === view.currency)!.label,
            onRemove: () => onView({ currency: 'ALL' }),
          },
        ]),
    ...((view.from ?? view.to)
      ? [
          {
            key: 'range',
            label: formatCivilRange({ from: view.from, to: view.to }, 'Qualquer dia'),
            onRemove: () => onView({ from: null, to: null }),
          },
        ]
      : []),
  ];

  return (
    <section aria-labelledby="company-services-title">
      <PageHeader
        trail="Catálogo / Serviços"
        id="company-services-title"
        title="Serviços"
        description="O catálogo do que a empresa oferece, com duração e preço de referência."
        actions={
          <>
            {capabilities.canCreate ? (
              <CreateButton onClick={() => setCreateOpen(true)}>Cadastrar serviço</CreateButton>
            ) : null}
          </>
        }
      />

      <FilterBar
        searchId="service-search"
        searchLabel="Buscar serviço"
        searchPlaceholder="Nome ou descrição"
        search={view.search}
        onSearchChange={(search) => onView({ search }, { replace: true })}
        chips={chips}
        onClear={() => onView({ activity: 'ALL', currency: 'ALL', from: null, to: null })}
        count={`${total} ${total === 1 ? 'serviço' : 'serviços'}`}
        resultsLabel={`Ver ${total} ${total === 1 ? 'serviço' : 'serviços'}`}
        pageSize={view.limit}
        onPageSizeChange={(limit) => onView({ limit })}
      >
        <FilterGroup
          label="Situação"
          description="Filtrar serviços por situação"
          options={activityOptions}
          value={view.activity}
          onChange={(activity) => onView({ activity })}
        />
        <FilterGroup
          label="Moeda"
          description="Filtrar serviços por moeda"
          options={currencyOptions}
          value={view.currency}
          onChange={(currency) => onView({ currency })}
        />
        <div className="min-w-0">
          <span className="mb-1.5 block text-xs font-semibold text-content/70">Cadastro</span>
          <DateRangePicker
            label="Dias de cadastro"
            value={{ from: view.from, to: view.to }}
            onValueChange={(next) => onView({ from: next.from, to: next.to })}
            today={todayCivilDate(timeZone)}
          />
        </div>
      </FilterBar>

      {page.isLoading ? (
        <LoadingCard label="Carregando serviços" />
      ) : page.error ? (
        <ErrorCard message={getApiError(page.error)} />
      ) : total === 0 ? (
        <EmptyCard
          icon={Sparkles}
          title="Nenhum serviço encontrado"
          description="Ajuste os filtros ou cadastre o primeiro serviço da empresa."
        />
      ) : (
        <div className="hue-responsive-collection overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          <CollectionCards>
            <div className="hue-collection-cards">
              <SortBar
                label="Ordenação dos serviços"
                columns={columns}
                sort={view.sort}
                direction={view.direction}
                onToggle={toggleSort}
              />
              <ul className="divide-y divide-surface-border">
                {page.data?.services.map((service) => (
                  <li key={service.id}>
                    <ServiceCard
                      service={service}
                      capabilities={capabilities}
                      onEdit={() => onView({ service: service.id })}
                      onActivation={() => setActivationTarget(service)}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </CollectionCards>
          <CollectionTable>
            <table className="w-full table-fixed border-collapse text-left">
              <caption className="sr-only">Serviços oferecidos pela empresa</caption>
              <thead>
                <tr className="border-b border-surface-border">
                  {columns.map((column) => (
                    <SortableColumn
                      key={column.sort}
                      column={column.sort}
                      label={column.label}
                      sort={view.sort}
                      direction={view.direction}
                      onToggle={toggleSort}
                    />
                  ))}
                  <th className="w-32">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {page.data?.services.map((service) => (
                  <tr key={service.id}>
                    <td className="px-5 py-4">
                      <strong className="block truncate text-sm">{service.displayName}</strong>
                      <span className="hue-list-description mt-1 block truncate text-xs text-content-muted">
                        {service.description ?? 'Sem descrição'}
                      </span>
                      {service.active ? null : <StatusPill tone="neutral">Arquivado</StatusPill>}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {formatDuration(service.durationMinutes)}
                    </td>
                    <td className="px-5 py-4 text-xs font-semibold tabular-nums text-content">
                      {formatCurrency(service.basePriceCents, service.currency)}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {formatDate(service.createdAt, timeZone)}
                    </td>
                    <td className="px-3 py-4">
                      <Actions
                        service={service}
                        capabilities={capabilities}
                        onEdit={() => onView({ service: service.id })}
                        onActivation={() => setActivationTarget(service)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CollectionTable>
          <Pagination
            total={total}
            limit={view.limit}
            offset={view.offset}
            onOffsetChange={(offset) => onView({ offset })}
            itemLabel={(count) => (count === 1 ? 'serviço' : 'serviços')}
          />
        </div>
      )}

      <ServiceDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        service={null}
        units={units}
        capabilities={capabilities}
        onSaved={async () => {
          await queryClient.invalidateQueries({ queryKey: ['company', 'services'] });
          setCreateOpen(false);
          snackbar.success('Serviço cadastrado.');
        }}
      />
      <ServiceDialog
        open={Boolean(view.service)}
        onOpenChange={(open) => {
          if (!open) onView({ service: undefined });
        }}
        service={detail.data ?? null}
        units={units}
        extras={detail.data && serviceExtras ? serviceExtras(detail.data) : null}
        loading={detail.isLoading}
        {...(detail.error ? { error: getApiError(detail.error) } : {})}
        capabilities={capabilities}
        onSaved={async () => {
          await queryClient.invalidateQueries({ queryKey: ['company', 'services'] });
          onView({ service: undefined });
          snackbar.success('Serviço atualizado.');
        }}
      />
      {activationTarget ? (
        <Modal
          open
          onOpenChange={() => setActivationTarget(undefined)}
          title={activationTarget.active ? 'Arquivar serviço' : 'Reativar serviço'}
          description={
            activationTarget.active
              ? 'O serviço deixa de aparecer na operação, mas o cadastro e o histórico permanecem.'
              : 'O serviço volta a ficar disponível para a operação.'
          }
        >
          <p className="text-sm">
            {activationTarget.active ? 'Arquivar' : 'Reativar'}{' '}
            <strong>{activationTarget.displayName}</strong>?
          </p>
          {activationError ? <RequestAlert message={activationError} /> : null}
          <div className="mt-7 flex justify-end gap-3 border-t border-surface-border pt-5">
            <Button variant="outline" onClick={() => setActivationTarget(undefined)}>
              Cancelar
            </Button>
            <Button onClick={() => void setActive(activationTarget)}>
              {activationTarget.active ? 'Arquivar serviço' : 'Reativar serviço'}
            </Button>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}

function ServiceCard({
  service,
  capabilities,
  onEdit,
  onActivation,
}: {
  service: ServiceOffering;
  capabilities: ServiceCapabilities;
  onEdit: () => void;
  onActivation: () => void;
}) {
  return (
    <div className="p-4">
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-400">
          <Sparkles className="size-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <strong className="block truncate text-sm">{service.displayName}</strong>
          <span className="hue-list-description mt-1 block truncate text-xs text-content-muted">
            {service.description ?? 'Sem descrição'}
          </span>
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-content-muted">
            <span className="inline-flex items-center gap-1">
              <Clock3 className="size-3.5 text-brand-400" aria-hidden="true" />
              {formatDuration(service.durationMinutes)}
            </span>
            <span className="inline-flex items-center gap-1 font-semibold tabular-nums text-content">
              <Coins className="size-3.5 text-brand-400" aria-hidden="true" />
              {formatCurrency(service.basePriceCents, service.currency)}
            </span>
          </div>
          {service.active ? null : (
            <div className="mt-2">
              <StatusPill tone="neutral">Arquivado</StatusPill>
            </div>
          )}
        </div>
        <Actions
          service={service}
          capabilities={capabilities}
          onEdit={onEdit}
          onActivation={onActivation}
        />
      </div>
    </div>
  );
}

function Actions({
  service,
  capabilities,
  onEdit,
  onActivation,
}: {
  service: ServiceOffering;
  capabilities: ServiceCapabilities;
  onEdit: () => void;
  onActivation: () => void;
}) {
  const canActivate = service.active ? capabilities.canArchive : capabilities.canUpdate;
  const { onSell } = capabilities;
  return (
    <div className="flex justify-end gap-1">
      {onSell && service.active ? (
        <IconButton
          label={`Vender ${service.displayName}`}
          icon={<ShoppingBag className="size-4" />}
          onClick={() => onSell(service)}
        />
      ) : null}
      {capabilities.canUpdate ? (
        <IconButton
          label="Editar serviço"
          icon={<PencilLine className="size-4" />}
          onClick={onEdit}
        />
      ) : null}
      {canActivate ? (
        <IconButton
          label={service.active ? 'Arquivar' : 'Reativar'}
          {...(service.active ? { tone: 'danger' as const } : {})}
          icon={service.active ? <Archive className="size-4" /> : <RotateCcw className="size-4" />}
          onClick={onActivation}
        />
      ) : null}
    </div>
  );
}

function ServiceDialog({
  open,
  onOpenChange,
  service,
  units,
  loading = false,
  error,
  capabilities,
  onSaved,
  extras = null,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  service: ServiceOffering | null;
  units: readonly ServiceUnit[];
  extras?: ReactNode;
  loading?: boolean;
  error?: string;
  capabilities: ServiceCapabilities;
  onSaved: () => Promise<void>;
}) {
  const [requestError, setRequestError] = useState<string>();
  const form = useForm<ServiceForm>({
    resolver: zodResolver(serviceSchema),
    defaultValues: emptyService(),
  });
  const currency = useWatch({ control: form.control, name: 'currency' });
  const basePriceCents = useWatch({ control: form.control, name: 'basePriceCents' });
  const durationMinutes = useWatch({ control: form.control, name: 'durationMinutes' });
  const unitTerms = useWatch({ control: form.control, name: 'unitTerms' });
  useEffect(() => {
    if (open) form.reset(service ? formOf(service) : emptyService());
  }, [form, open, service]);

  async function save(data: ServiceForm) {
    setRequestError(undefined);
    // Unidade que esta pessoa não vê guarda o que já tinha: a lista enviada substitui a inteira.
    const shown = new Set(units.map((unit) => unit.id));
    const payload: ServiceInput = {
      ...data,
      description: data.description || null,
      unitTerms: [
        ...(service?.unitTerms ?? []).filter((terms) => !shown.has(terms.businessUnitId)),
        ...data.unitTerms.filter(
          (terms) =>
            shown.has(terms.businessUnitId) &&
            (terms.priceCents !== null || terms.durationMinutes !== null),
        ),
      ].sort((left, right) => left.businessUnitId.localeCompare(right.businessUnitId)),
    };
    try {
      if (service) await servicesApi.update(service.id, payload);
      else await servicesApi.create(payload);
      await onSaved();
    } catch (cause) {
      setRequestError(getApiError(cause));
    }
  }

  const disabled = Boolean(service) && !capabilities.canUpdate;
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={service ? 'Editar serviço' : 'Cadastrar serviço'}
      description="Duração e preço são referências atuais; cada atendimento futuro preservará os valores usados."
    >
      {loading ? (
        <LoadingCard label="Carregando serviço" />
      ) : error ? (
        <ErrorCard message={error} />
      ) : (
        <form noValidate onSubmit={(event) => void form.handleSubmit(save)(event)}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Input
                label="Nome do serviço"
                error={form.formState.errors.displayName?.message}
                disabled={disabled}
                {...form.register('displayName')}
              />
            </div>
            <Input
              label="Duração em minutos"
              type="number"
              inputMode="numeric"
              min={5}
              max={1440}
              step={5}
              error={form.formState.errors.durationMinutes?.message}
              disabled={disabled}
              {...form.register('durationMinutes', { valueAsNumber: true })}
            />
            <Select
              label="Moeda"
              value={currency}
              options={formCurrencyOptions}
              onValueChange={(value) =>
                form.setValue('currency', value as Currency, { shouldValidate: true })
              }
              disabled={disabled}
            />
            <div className="sm:col-span-2">
              <CurrencyInput
                label="Preço-base"
                currency={currency}
                value={basePriceCents}
                onValueChange={(value) =>
                  form.setValue('basePriceCents', value ?? 0, { shouldValidate: true })
                }
                error={form.formState.errors.basePriceCents?.message}
                disabled={disabled}
                hint="Use zero para serviços gratuitos."
              />
            </div>
            {units.length > 1 ? (
              <ServiceUnitTermsFields
                units={units}
                value={unitTerms}
                onChange={(next) => form.setValue('unitTerms', next)}
                currency={currency}
                basePriceCents={basePriceCents}
                durationMinutes={Number.isFinite(durationMinutes) ? durationMinutes : 0}
                disabled={disabled}
              />
            ) : null}
            <div className="sm:col-span-2">
              <Textarea
                label="Descrição"
                rows={4}
                error={form.formState.errors.description?.message}
                disabled={disabled}
                {...form.register('description')}
              />
            </div>
          </div>
          {requestError ? <RequestAlert message={requestError} /> : null}
          {(!service && capabilities.canCreate) || (service && capabilities.canUpdate) ? (
            <div className="mt-6 flex flex-wrap items-center justify-end gap-3 border-t border-surface-border pt-5">
              <FormErrorSummary className="basis-full sm:basis-auto sm:flex-1" />
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button loading={form.formState.isSubmitting}>
                {service ? 'Salvar serviço' : 'Cadastrar serviço'}
              </Button>
            </div>
          ) : null}
        </form>
      )}
      {extras && !loading && !error ? (
        <div className="mt-6 border-t border-surface-border pt-6">{extras}</div>
      ) : null}
    </Modal>
  );
}

function emptyService(): ServiceForm {
  return {
    displayName: '',
    description: '',
    durationMinutes: 60,
    basePriceCents: 0,
    currency: 'BRL',
    unitTerms: [],
  };
}

function formOf(service: ServiceOffering): ServiceForm {
  return {
    displayName: service.displayName,
    description: service.description ?? '',
    durationMinutes: service.durationMinutes,
    basePriceCents: service.basePriceCents,
    currency: service.currency,
    unitTerms: service.unitTerms ?? [],
  };
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}min` : `${hours}h`;
}
